import { useState } from 'react'
import { api, type RuleHit } from '../api'
import { Cited } from '../components/Cited'
import { FundPicker, useFunds } from '../components/FundPicker'
import { NotEvaluated } from '../components/NotEvaluated'
import { Origin } from '../components/Origin'
import { layerLabel, says } from '../limits'
import { DeskCell, DeskHeader, useDesks } from '../desks'
import { sortRows, useSort } from '../components/Grid'

/**
 * Search across rules — proposal §D, TECHNICAL_DESIGN.md §3.2.
 *
 * Proposals are available and **off by default**, and they arrive labelled with
 * their status: §8.6 means a limit awaiting approval is not a limit, so a
 * pending rule mixed silently into the results would read as one. Rejected rules
 * are never returned at all, which the API guarantees rather than this file.
 *
 * **The screen says what it searches.** It was a box, a checkbox and a button,
 * and the box's placeholder was the only hint that `Schedule 2` was a sensible
 * thing to type — so the reader had to guess whether this searched rule keys,
 * fund names, document titles or the text of a mandate. It searches the rule's
 * own words: its key, measure, basis and subject at full weight, and its clause
 * reference, template and narrative text underneath. Saying so costs a
 * paragraph and removes the guessing.
 *
 * **The button waits for a query.** Enabled over an empty box it produced a 422
 * from an API that requires one character — an error message about a query
 * parameter, in answer to a click the screen had invited.
 *
 * **Narrowing to a fund is offered**, because it is the second question every
 * time: *does anything anywhere say this* is asked once, and *does anything on
 * this fund say this* is asked all afternoon. The API has always taken
 * `portfolio_id` and no screen ever passed it. Narrowing includes what the fund
 * inherits — a limit reaching it through a group is a limit on it.
 *
 * **Results are a table**, and each row says which fund it is on. A firm-wide
 * search whose results cannot say what they are attached to answers half the
 * question and leaves the reader to open each hit to find the other half.
 */
export function Search() {
  const { funds } = useFunds()
  const [query, setQuery] = useState('')
  /** The desk the hits are narrowed to, for somebody across the whole firm (decision 91). */
  const [desk, setDesk] = useState('')
  const { sort, onSort } = useSort()
  const desks = useDesks()
  const deskOf = (h: RuleHit) => (h.portfolio_code ? desks.fund(h.portfolio_code) : desks.group(h.origin)) ?? ''
  const [fundId, setFundId] = useState('')
  const [includeProposed, setIncludeProposed] = useState(false)
  const [hits, setHits] = useState<RuleHit[] | null>(null)
  /** What produced the results on screen, so the empty state can talk about the
   *  search that was actually run rather than about whatever is in the box now. */
  const [ran, setRan] = useState<{ query: string; proposed: boolean } | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const ready = query.trim().length > 0

  async function run(event: React.FormEvent) {
    event.preventDefault()
    if (!ready) return
    setBusy(true)
    setError(null)
    try {
      setHits(await api.searchRules(query.trim(), fundId || undefined, includeProposed))
      setRan({ query: query.trim(), proposed: includeProposed })
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="search-page">
      <header className="page-head">
        <h2>Search</h2>
        <p className="note">
          Find a limit anywhere in the firm by what it says. This searches the
          rule itself — its key, the measure and basis it is written in, what it
          applies to, the clause it cites, and the wording of a limit recorded as
          text. To find a fund, use Lookup; to find a document, Mandate documents.
        </p>
      </header>

      <form className="filters" onSubmit={run}>
        <label>
          Search rules
          <input
            className="wide"
            value={query}
            placeholder="duration, corporates, Sch. 2 para 4(b)…"
            title={
              'A phrase in quotes matches those words together — "off benchmark". ' +
              'A word with a minus in front excludes it — duration -house.'
            }
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        {/* The second question, every time. The API has always taken a portfolio
            and no screen ever passed one. */}
        <FundPicker
          label="Within"
          value={fundId}
          onChange={setFundId}
          funds={funds}
          placeholder="Anywhere in the firm"
          testId="within"
        />
        <label className="toggle">
          <input
            type="checkbox"
            checked={includeProposed}
            onChange={(e) => setIncludeProposed(e.target.checked)}
          />
          Include limits still awaiting approval
        </label>
        <button type="submit" disabled={!ready || busy}>
          {busy ? 'Searching…' : 'Search'}
        </button>
      </form>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {!hits && !error && (
        <p className="empty">
          Type something to search for. Quotes match a phrase —{' '}
          <code>&quot;off benchmark&quot;</code> — and a minus excludes a word —{' '}
          <code>duration -house</code>.
        </p>
      )}

      {hits && (
        <>
          <NotEvaluated />
          <p className="count">
            {hits.length} result(s) for <strong>{ran?.query}</strong>
            {fundId && funds.find((f) => f.id === fundId)
              ? ` on ${funds.find((f) => f.id === fundId)!.code}, including what it inherits`
              : ''}
          </p>

          {hits.length === 0 ? (
            <p className="empty" data-testid="no-hits">
              Nothing matches those words.
              {/* The commonest reason for a miss on a store mid-migration, and
                  the reader has no way to know the toggle exists unless the
                  moment it would have helped says so. */}
              {!ran?.proposed && ' Limits still awaiting approval are not being searched — tick the box above to include them.'}
            </p>
          ) : (
            <table className="grid limits has-menus">
              <thead>
                <tr>
                  <DeskHeader sort={sort} onSort={onSort} value={desk} onChange={setDesk} />
                  <th>Portfolio</th>
                  <th>Limit</th>
                  <th>Says</th>
                  <th>Set by</th>
                  <th>From</th>
                </tr>
              </thead>
              <tbody>
                {sortRows(
                  hits.filter((h) => !desk || deskOf(h) === desk),
                  sort,
                  { desk: deskOf },
                ).map((hit) => {
                  const said = says(hit)
                  return (
                    <tr key={hit.id} data-testid="hit">
                      <DeskCell fund={hit.portfolio_code} group={hit.origin} />
                      <td>
                        <span className="portfolio">
                          {hit.portfolio_code ?? hit.origin ?? '—'}
                        </span>
                        {/* Never silently: §8.6 means a proposal is not a limit. */}
                        {hit.status !== 'active' && (
                          <span className="status" data-testid="status">
                            {hit.status} — not in force
                          </span>
                        )}
                      </td>
                      <td>
                        <code>{hit.rule_key}</code>
                      </td>
                      <td>
                        <span className={said.numeric ? 'limit' : 'no-number'}>
                          {said.text}
                        </span>
                      </td>
                      <td>
                        <span className="pill pill-layer">{layerLabel(hit.layer)}</span>
                      </td>
                      <td>
                        <Origin rule={hit} />
                        <Cited rule={hit} />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  )
}
